import { useRef } from 'react';
import { Workbook } from '@fortune-sheet/react';
import { BottomDrawer } from '../BottomDrawer/BottomDrawer';
import Button from '../_custom/Button/Button';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { useSheetEditorDrawerLogic } from './useSheetEditorDrawerLogic';
import { FortuneSheet } from './types';
import { useColumnAutofit } from './useColumnAutofit';
import styles from './SheetEditorDrawer.module.scss';
import '@fortune-sheet/react/dist/index.css';

export interface SheetEditorDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Original file name; used as the drawer title and download base name. */
    fileName: string;
    /** In-memory sheets (import / composed). When provided, renders directly — skips URL fetch. */
    sheets?: FortuneSheet[];
    /** Presigned URL of the xlsx/csv file to edit (legacy fetch path). */
    fileUrl?: string;
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Existing saved-document id — passed through to the save payload for update. */
    docId?: string;
    /** Server-assigned generation-lineage id — carried through so a new version
     *  saved from this drawer groups with its siblings (mirrors the Plate
     *  editor's persist path in ReportEditorRoute). */
    genId?: string;
    /** Save context. When absent (e.g. the read-only DataRoom viewer) the Save
     *  action is hidden and the drawer is view + download only. */
    assesseeOrgId?: string;
    investorOrgId?: string;
    createdBy?: string;
    templateId?: string;
    docType?: string;
    name?: string;
    description?: string;
    /** Called with the saved document after a successful save. */
    onSaved?: (doc: any) => void;
}

// Fortune-Sheet's default toolbar with the one-click currency button removed.
// Currency formatting is still reachable via the "format" dropdown; we drop the
// quick button per product (no multi-currency need). currency="₹" makes the
// Format dropdown default to the Indian Rupee instead of the library default (¥).
const TOOLBAR_ITEMS = [
    'undo',
    'redo',
    'format-painter',
    'clear-format',
    '|',
    'percentage-format',
    'number-decrease',
    'number-increase',
    'format',
    '|',
    'font',
    '|',
    'font-size',
    '|',
    'bold',
    'italic',
    'strike-through',
    'underline',
    '|',
    'font-color',
    'background',
    'border',
    'merge-cell',
    '|',
    'horizontal-align',
    'vertical-align',
    'text-wrap',
    'text-rotation',
    '|',
    'freeze',
    'conditionFormat',
    'filter',
    'link',
    'image',
    'comment',
    'quick-formula',
    'dataVerification',
    'splitColumn',
    'locationCondition',
    'screenshot',
    'search',
];

/**
 * Editable spreadsheet drawer. Accepts either in-memory FortuneSheet[] (from
 * import or compose) or a presigned URL to fetch. Renders a Fortune-Sheet
 * grid (formulas + formula bar built in) inside the same BottomDrawer the
 * PlateEditor uses. Header actions: Save (persists to GeneratedDocument),
 * Download .csv, Download .xlsx.
 */
const SheetEditorDrawer: React.FC<SheetEditorDrawerProps> = ({
    open,
    onClose,
    fileName,
    sheets: inMemorySheets,
    fileUrl,
    mimeType,
    docId,
    genId,
    assesseeOrgId,
    investorOrgId,
    createdBy,
    templateId,
    docType,
    name,
    description,
    onSaved,
}) => {
    const { sheets, loading, error, saving, workbookRef, handleDownloadXlsx, handleDownloadCsv, handleSave } = useSheetEditorDrawerLogic({
        open,
        fileName,
        sheets: inMemorySheets,
        fileUrl,
        mimeType,
        docId,
        genId,
        assesseeOrgId,
        investorOrgId,
        createdBy,
        templateId,
        docType,
        name,
        description,
        onSaved,
    });

    // Save is only available with a persistence context (Reports). The read-only
    // DataRoom viewer passes no assesseeOrgId → view + download only.
    const canSave = Boolean(assesseeOrgId);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const { fitColumnsToolbarItem } = useColumnAutofit({ workbookRef, wrapperRef, enabled: !loading && !error && !!sheets?.length });
    const headerRight =
        !loading && !error ? (
            <div className={styles.actions}>
                <Button type="button" text="Download .csv" variant="secondary" className={styles.headerBtn} onClick={handleDownloadCsv} />
                <Button type="button" text="Download .xlsx" variant="secondary" className={styles.headerBtn} onClick={handleDownloadXlsx} />
                {canSave && (
                    <Button
                        type="button"
                        text="Save"
                        variant="purple"
                        className={styles.headerBtn}
                        onClick={handleSave}
                        disabled={saving}
                    />
                )}
            </div>
        ) : null;

    // Use fileUrl as the workbook key on the fetch path; fall back to fileName
    // so in-memory sheets get a stable key that doesn't remount on every render.
    const workbookKey = fileUrl ?? fileName;

    return (
        <BottomDrawer
            open={open}
            onClose={onClose}
            title={fileName}
            headerRight={headerRight}
            className={styles.drawer}
            bodyClassName={styles.body}
            height="100vh"
        >
            {loading && (
                <div className={styles.statusBox}>
                    <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                </div>
            )}
            {error && (
                <div className={styles.statusBox}>
                    <p>Unable to open this file for editing.</p>
                </div>
            )}
            {!loading && !error && sheets && (
                <div className={styles.workbookWrapper} ref={wrapperRef}>
                    <Workbook
                        key={workbookKey}
                        ref={workbookRef}
                        data={sheets}
                        toolbarItems={TOOLBAR_ITEMS}
                        customToolbarItems={[fitColumnsToolbarItem]}
                        currency="₹"
                        showToolbar
                        showFormulaBar
                        showSheetTabs
                        allowEdit
                    />
                </div>
            )}
        </BottomDrawer>
    );
};

export default SheetEditorDrawer;
